import { Product, formatEur, formatPct } from "@/lib/index";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Trash2, Plus } from "lucide-react";
import { motion } from "framer-motion";

interface ProductsTableProps {
  products: Product[];
  p1Tariffs: unknown;
  p2Tariffs: unknown;
  onUpdateProduct: (id: string, field: keyof Product, value: string | number) => void;
  onDeleteProduct: (id: string) => void;
  onAddProduct: () => void;
}

export function ProductsTable({
  products,
  onUpdateProduct,
  onDeleteProduct,
  onAddProduct
}: ProductsTableProps) {
  const calculateCost = (units: number, price: number) => units * price;
  const calculateDifference = (costP1: number, costP2: number) => costP1 - costP2;
  const calculateSavingsPct = (costP1: number, costP2: number) => {
    if (costP2 === 0) return 0;
    return ((costP2 - costP1) / costP2) * 100;
  };

  const totals = products.reduce(
    (acc, product) => {
      const costP1 = calculateCost(product.units, product.priceP1);
      const costP2 = calculateCost(product.units, product.priceP2);
      return {
        costP1: acc.costP1 + costP1,
        costP2: acc.costP2 + costP2
      };
    },
    { costP1: 0, costP2: 0 }
  );

  const totalDifference = calculateDifference(totals.costP1, totals.costP2);
  const totalSavingsPct = calculateSavingsPct(totals.costP1, totals.costP2);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="w-full"
    >
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Producto</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-foreground">Unidades/año</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">
                  Factura P1 <span className="text-xs font-normal text-muted-foreground">(€/u)</span>
                </th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">
                  Factura P2 <span className="text-xs font-normal text-muted-foreground">(€/u)</span>
                </th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">
                  Coste P1
                </th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">
                  Coste P2
                </th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">Diferencia</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-foreground">Ahorro%</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-foreground">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product, index) => {
                const costP1 = calculateCost(product.units, product.priceP1);
                const costP2 = calculateCost(product.units, product.priceP2);
                const difference = calculateDifference(costP1, costP2);
                const savingsPct = calculateSavingsPct(costP1, costP2);

                return (
                  <motion.tr
                    key={product.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="border-b border-border hover:bg-muted/30 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <Input
                        value={product.name}
                        onChange={(e) => onUpdateProduct(product.id, 'name', e.target.value)}
                        className="h-9 text-sm"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <Input
                        type="number"
                        value={product.units}
                        onChange={(e) => onUpdateProduct(product.id, 'units', Number(e.target.value))}
                        className="h-9 w-24 text-center text-sm"
                        min="0"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <Input
                        type="number"
                        value={product.priceP1}
                        onChange={(e) => onUpdateProduct(product.id, 'priceP1', Number(e.target.value))}
                        className="h-9 text-right text-sm"
                        step="0.01"
                        min="0"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <Input
                        type="number"
                        value={product.priceP2}
                        onChange={(e) => onUpdateProduct(product.id, 'priceP2', Number(e.target.value))}
                        className="h-9 text-right text-sm"
                        step="0.01"
                        min="0"
                      />
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-mono text-muted-foreground">
                      {formatEur(costP1)}
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-mono text-muted-foreground">
                      {formatEur(costP2)}
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-mono font-semibold">
                      <span className={difference < 0 ? 'text-success' : 'text-destructive'}>
                        {formatEur(difference)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Badge
                        variant={savingsPct > 0 ? 'default' : 'destructive'}
                        className={`font-mono text-xs ${savingsPct > 0 ? 'bg-success text-success-foreground' : ''}`}
                      >
                        {formatPct(savingsPct)}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => onDeleteProduct(product.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </motion.tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-border bg-muted/30 font-semibold">
                <td className="px-4 py-4 text-sm text-foreground">TOTAL</td>
                <td className="px-4 py-4 text-center text-sm text-muted-foreground">
                  {products.reduce((sum, p) => sum + p.units, 0)}
                </td>
                <td className="px-4 py-4"></td>
                <td className="px-4 py-4"></td>
                <td className="px-4 py-4 text-right text-sm font-mono text-foreground">
                  {formatEur(totals.costP1)}
                </td>
                <td className="px-4 py-4 text-right text-sm font-mono text-foreground">
                  {formatEur(totals.costP2)}
                </td>
                <td className="px-4 py-4 text-right text-sm font-mono font-bold">
                  <span className={totalDifference < 0 ? 'text-success' : 'text-destructive'}>
                    {formatEur(totalDifference)}
                  </span>
                </td>
                <td className="px-4 py-4 text-center">
                  <Badge
                    variant={totalSavingsPct > 0 ? 'default' : 'destructive'}
                    className={`font-mono text-xs ${totalSavingsPct > 0 ? 'bg-success text-success-foreground' : ''}`}
                  >
                    {formatPct(totalSavingsPct)}
                  </Badge>
                </td>
                <td className="px-4 py-4"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Card>
      <div className="mt-4 flex justify-center">
        <Button
          onClick={onAddProduct}
          className="gap-2"
          size="lg"
        >
          <Plus className="h-4 w-4" />
          Añadir Producto
        </Button>
      </div>
    </motion.div>
  );
}